import { Loading, TableCard, TableOutput } from "@general-backoffice/core";
import type { PaginationMeta, User } from "@investhinks/api-client";
import classNames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, Card, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { canImpersonateUser, startImpersonation } from "../../utils/impersonation";
import { TypedTable } from "../../utils/typedLib";
import PaginatedTableFooter from "../shared/PaginatedTableFooter";
import TriCheckbox from "../shared/TriCheckbox";

interface UsersListCardProps {
  users?: User[];
  totalUsers?: number;
  onUserClick?: (user: User) => void;
  isLoading: boolean;
  pagination?: PaginationMeta | null;
  goToPage?: (page: number) => void;
  changePageSize?: (size: number) => void;
  filters?: Record<string, unknown>;
  changeFilters?: (key: string, value: unknown) => void;
  filtersToShow?: string[];
  sort?: Record<string, string | null>;
  changeSort?: (field: string, direction: string | null) => void;
  title?: string;
  columnsToHide?: string[];
  /**
   * Ids of tiers whose fees are all zero. Resolved by the caller from the full
   * tier list, because the tier populated on a user omits its dynamic fees, so
   * zero-fee cannot be decided from the user alone.
   */
  zeroFeeTierIds?: (number | string)[];
}

const UsersListCard: React.FC<UsersListCardProps> = ({
  users = [],
  totalUsers,
  onUserClick,
  isLoading,
  pagination,
  goToPage,
  changePageSize,
  filters,
  changeFilters,
  filtersToShow = [],
  sort,
  changeSort,
  title,
  columnsToHide = [],
  zeroFeeTierIds = [],
}) => {
  const { t } = useTranslation("users", { keyPrefix: "list" });
  // `sort`/`changeSort` were accepted and then thrown away, so the table
  // advertised a capability it did not have. Columns opt in through this
  // helper, and stay inert when no handler is supplied.
  const sortable = (field: string) =>
    changeSort ? { sort: (d: string | null) => changeSort(field, d), sortStatus: sort?.[field] } : {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userToImpersonate, setUserToImpersonate] = useState<User | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const hideColumns = <T,>(field: string, column: T): T[] => (columnsToHide.includes(field) ? [] : [column]);

  const handleImpersonate = async () => {
    if (!userToImpersonate) return;

    setIsImpersonating(true);
    const success = await startImpersonation(userToImpersonate.id, dispatch, navigate);
    setIsImpersonating(false);

    if (success) {
      setUserToImpersonate(null);
    }
  };

  const columns = [
    {
      compressed: true,
      name: t("user"),
      ...sortable("name"),
      formattedContent: ({ email, name }: User) => (
        <>
          <div>{name}</div>
          <small className="font-weight-bold">{email}</small>
        </>
      ),
      isTh: true,
    },
    ...hideColumns("badges", {
      formattedContent: (user: User) => {
        const { role, blocked } = user;
        const bookmarks: Parameters<typeof TableOutput.Bookmarks>[0]["bookmarks"] = [];

        // Shown as two separate badges on purpose: the legacy isVIP flag is
        // being deprecated in favour of zero-fee tiers, so the two must stay
        // distinguishable while users are migrated across. A user can carry
        // both.
        if (user.isVIP) {
          bookmarks.push({
            icon: t("badges.vip"),
            title: t("badges.vipLegacy"),
            color: "warning",
          });
        }

        const tierId = user.subscription_tier?.id;
        if (tierId !== undefined && zeroFeeTierIds.some((id) => `${id}` === `${tierId}`)) {
          bookmarks.push({
            icon: t("badges.zeroFee"),
            title: t("badges.zeroFeeTier", { tier: user.subscription_tier?.name ?? "" }),
            color: "info",
          });
        }

        if (role?.name !== "Authenticated") {
          bookmarks.push({
            icon: <i className="fas fa-user-times" />,
            title: t("badges.notVerified"),
            color: "danger",
          });
        }

        if (blocked) {
          bookmarks.push({
            icon: <i className="fas fa-lock" />,
            title: t("badges.blocked"),
            color: "danger",
          });
        }
        return <TableOutput.Bookmarks bookmarks={bookmarks} />;
      },
    }),
    ...hideColumns("bots", {
      name: t("numBots"),
      ...sortable("numBots"),
      formattedContent: ({ exchange_accounts }: User) => {
        let numActive = 0;
        let numInactive = 0;

        (exchange_accounts ?? []).forEach(({ bot_configs }) => {
          (bot_configs ?? []).forEach(({ disabled }) => {
            if (disabled) numInactive++;
            else numActive++;
          });
        });
        return (
          <div style={{ fontSize: "1rem" }}>
            <Badge className="p-2" {...(!!numActive && { color: "success" })}>
              <span className="mr-1">{numActive}</span>
              <i className="fas fa-play" />
            </Badge>
            <Badge className="p-2" {...(!!numInactive && { color: "danger" })}>
              <span className="mr-1">{numInactive}</span>
              <i className="fas fa-pause" />
            </Badge>
          </div>
        );
      },
    }),
    ...hideColumns("referrer", {
      name: t("referrer"),
      formattedContent: (props: User) => {
        const referrer = props?.referral?.referrer;
        if (!referrer) return t("noReferrer");
        return (
          <Link to={`/users/${referrer?.id}`} className="text-default">
            <div>{referrer?.name}</div>
            <small className="font-weight-bold">{referrer?.email}</small>
          </Link>
        );
      },
    }),
    ...hideColumns("partnerType", {
      name: t("partnerType"),
      formattedContent: ({ referrerType }: User) => (
        <span
          className={classNames("text-capitalize", {
            "font-weight-bold": referrerType && referrerType !== "DEFAULT",
          })}
        >
          {(referrerType || "").toLowerCase()}
        </span>
      ),
    }),
    ...hideColumns("tier", {
      name: t("tier"),
      ...sortable("tier"),
      formattedContent: ({ subscription_tier }: User) =>
        subscription_tier ? (
          <Badge color={subscription_tier.isDefault ? "warning" : "info"}>
            {subscription_tier.isDefault && <i className="fas fa-star mr-1" />}
            {subscription_tier.name}
          </Badge>
        ) : (
          <span className="text-muted">{t("tierNone")}</span>
        ),
    }),
    ...hideColumns("referrals", {
      name: t("numReferrals"),
      ...sortable("numReferrals"),
      formattedContent: ({ referrals }: User) => {
        const safeReferrals = referrals ?? [];
        return (
          <div style={{ fontSize: "1rem" }}>
            <Badge className="p-2" color={safeReferrals.length > 0 ? "primary" : undefined}>
              <span className="mr-1">{safeReferrals.length || "0"}</span>
              <i className="fas fa-user" />
            </Badge>
          </div>
        );
      },
    }),
    {
      compressed: true,
      formattedContent: (user: User) => {
        return (
          <TableOutput.CustomActions
            actions={[
              ...(onUserClick
                ? [
                    {
                      icon: <i className="fas fa-table text-default" />,
                      onClick: () => onUserClick({ ...user }),
                      description: t("actions.seeUser"),
                    },
                  ]
                : []),
              ...(canImpersonateUser(user)
                ? [
                    {
                      icon: <i className="fas fa-user-secret text-warning" />,
                      onClick: () => setUserToImpersonate(user),
                      description: t("actions.loginAs"),
                    },
                  ]
                : []),
            ]}
          />
        );
      },
    },
  ];

  return (
    <Card className="overflow-hidden">
      <TableCard.Header
        title={title || t("title")}
        subtitle={t("total", { count: totalUsers })}
        search={{
          value: typeof filters?.["search"] === "string" ? filters["search"] : "",
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => changeFilters?.("search", e.target.value),
          msToChange: 1000,
          placeholder: t("search"),
        }}
      >
        {!!filtersToShow.length && (
          <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
            <div className="px-4 flex-center justify-content-end flex-wrap" style={{ gap: ".8rem 1rem" }}>
              {filtersToShow.includes("isPartner") && (
                <TriCheckbox
                  label={t("isPartner")}
                  state={typeof filters?.isPartner === "boolean" ? filters.isPartner : null}
                  changeState={(s) => changeFilters?.("isPartner", s)}
                  disabled={isLoading}
                />
              )}
            </div>
          </div>
        )}
      </TableCard.Header>
      {isLoading && <Loading.Line />}
      <TypedTable
        columns={columns}
        rows={users}
        isLoading={isLoading}
        noContentMessage={t("noUsers")}
        rowHeight={58}
        rowAmount={pagination?.pageSize || 1}
        onRowClick={onUserClick}
        selectable={false}
        sortable={false}
      />
      {pagination && goToPage && changePageSize && (
        <PaginatedTableFooter
          isLoading={isLoading}
          pagination={pagination}
          goToPage={goToPage}
          changePageSize={changePageSize}
        />
      )}

      <Modal isOpen={!!userToImpersonate} toggle={() => setUserToImpersonate(null)}>
        <ModalHeader toggle={() => setUserToImpersonate(null)}>{t("impersonateModal.title")}</ModalHeader>
        <ModalBody>
          {t("impersonateModal.message")} <strong>{userToImpersonate?.name || userToImpersonate?.email}</strong>?
          <div className="mt-3 text-muted small">{t("impersonateModal.hint")}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setUserToImpersonate(null)} disabled={isImpersonating}>
            {t("impersonateModal.cancel")}
          </Button>
          <Button color="warning" onClick={handleImpersonate} disabled={isImpersonating}>
            {isImpersonating ? (
              <>
                <i className="fas fa-spinner fa-spin mr-1" />
                {t("impersonateModal.loading")}
              </>
            ) : (
              <>
                <i className="fas fa-user-secret mr-1" />
                {t("impersonateModal.confirm")}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default UsersListCard;
